import { createStore, applyMiddleware, compose } from 'redux'
import createSagaMiddleware, { SagaMiddlewareOptions } from 'redux-saga'
import reduxLogger from 'redux-logger'
import { rootReducer } from './reducers'
import { rootSaga } from './sagas'
import * as Sentry from '@sentry/react'
import { logger } from './utils/logger'

const sentryReduxEnhancer = Sentry.createReduxEnhancer({})

/**
 * `redux-logger` prints every action and a before/after snapshot of the entire store to the
 * console. That is useful locally and a liability in production: anything a slice holds is
 * readable by whoever opens devtools on an admin's machine, which for this app has included
 * one-shot API credentials. Slices are written to keep secrets out of the store in the first
 * place, but this gate makes the console channel unavailable rather than merely unused.
 *
 * `process.env.NODE_ENV` is statically replaced by the shared Vite config, so the branch is
 * decided at build time and the middleware is never installed in production. Note this does not
 * remove `redux-logger` from the bundle — it is a static import and the package is not marked
 * side-effect-free, so Rollup keeps it either way (verified: its strings are still present with
 * the condition both inlined and hoisted). Dropping it would mean a dynamic import and an async
 * `configureStore`, which is not worth reshaping app bootstrap for a few KB.
 */
const isDevelopment = process.env.NODE_ENV !== 'production'

const configureStore = (initialState = {}) => {
  const sagaMiddlewareOptions: SagaMiddlewareOptions = {}

  const saga = createSagaMiddleware(sagaMiddlewareOptions)

  const middlewares = isDevelopment
    ? [saga, reduxLogger, logger.createReduxMiddleware()]
    : [saga, logger.createReduxMiddleware()]

  const store = createStore(
    rootReducer,
    initialState,
    compose(applyMiddleware(...middlewares), sentryReduxEnhancer),
  )

  saga.run(rootSaga)

  return { ...store }
}

export const store = configureStore()
